import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { ArrowRight, Cpu, Layers, Printer, Save, ScanLine, Tags, Boxes } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { LoadingSpinner } from '@/components/ui/loading-spinner'
import { IconLabel } from '@/components/ui/app-icon'
import { useToast } from '@/hooks/useToast'
import { extractApiError } from '@/lib/api/client'
import { settingsApi, type GeneralSettings } from '@/lib/api/misc'
import type { PrintMethod } from '@/types/settings'
import type { ScanDuplicateMode } from '@/types/task-scan'

export function SettingsPage() {
  const { toast } = useToast()
  const qc = useQueryClient()
  const toastRef = useRef(toast)
  toastRef.current = toast
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [cpuCount, setCpuCount] = useState(1)
  const [maxWorkersKm, setMaxWorkersKm] = useState('4')
  const [scanDuplicateMode, setScanDuplicateMode] = useState<ScanDuplicateMode>('reject')
  const [isSavingScan, setIsSavingScan] = useState(false)
  const [printMethod, setPrintMethod] = useState<PrintMethod>('agent')
  const [isSavingPrint, setIsSavingPrint] = useState(false)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      setIsLoading(true)
      try {
        const res = await settingsApi.get()
        const data = res.data as GeneralSettings
        if (cancelled) return
        setCpuCount(data.cpu_count ?? 1)
        const workers = data.performance?.max_workers_km ?? data.cpu_count ?? 4
        setMaxWorkersKm(String(workers))
        setScanDuplicateMode(data.scan_duplicate_mode === 'highlight' ? 'highlight' : 'reject')
        setPrintMethod(data.print_method === 'browser' ? 'browser' : 'agent')
      } catch (err) {
        if (!cancelled) toastRef.current.error(extractApiError(err))
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  const savePerformance = async () => {
    const parsed = parseInt(maxWorkersKm, 10)
    if (!Number.isFinite(parsed) || parsed < 1) {
      toast.error('Укажите число потоков от 1')
      return
    }
    setIsSaving(true)
    try {
      const res = await settingsApi.save({
        performance: { max_workers_km: parsed },
      })
      const data = res.data as GeneralSettings
      setCpuCount(data.cpu_count ?? cpuCount)
      const workers = data.performance?.max_workers_km ?? parsed
      setMaxWorkersKm(String(workers))
      toast.success('Настройки производительности сохранены')
    } catch (err) {
      toast.error(extractApiError(err))
    } finally {
      setIsSaving(false)
    }
  }

  const saveScanDuplicateMode = async () => {
    setIsSavingScan(true)
    try {
      const res = await settingsApi.save({ scan_duplicate_mode: scanDuplicateMode })
      const data = res.data as GeneralSettings
      setScanDuplicateMode(data.scan_duplicate_mode === 'highlight' ? 'highlight' : 'reject')
      toast.success('Настройка обработки дублей сохранена')
    } catch (err) {
      toast.error(extractApiError(err))
    } finally {
      setIsSavingScan(false)
    }
  }

  const savePrintMethod = async () => {
    setIsSavingPrint(true)
    try {
      const res = await settingsApi.save({ print_method: printMethod })
      const data = res.data as GeneralSettings
      setPrintMethod(data.print_method === 'browser' ? 'browser' : 'agent')
      void qc.invalidateQueries({ queryKey: ['general-settings'] })
      toast.success('Режим печати сохранён')
    } catch (err) {
      toast.error(extractApiError(err))
    } finally {
      setIsSavingPrint(false)
    }
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-20">
        <LoadingSpinner />
      </div>
    )
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">
          <IconLabel icon="settings">Настройки</IconLabel>
        </h1>
        <p className="text-sm text-gray-500 mt-1">Параметры системы</p>
      </div>

      <div className="space-y-4 max-w-4xl">
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-start gap-4">
            <div className="p-2 rounded-lg bg-violet-50 text-violet-600">
              <Cpu className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-semibold text-gray-900">Рендер КМ</h2>
              <p className="text-sm text-gray-600 mt-1">
                Параллельный рендер этикеток КМ при нажатии «Рендер КМ» в задании.
              </p>
              <p className="text-xs text-gray-500 mt-2">
                Логических ядер на сервере: <span className="font-medium text-gray-700">{cpuCount}</span>
              </p>
              <div className="mt-4 flex items-end gap-3 flex-wrap">
                <div>
                  <label htmlFor="max-workers-km" className="block text-sm font-medium text-gray-700 mb-1">
                    Потоков рендера КМ
                  </label>
                  <Input
                    id="max-workers-km"
                    type="number"
                    min={1}
                    max={64}
                    value={maxWorkersKm}
                    onChange={(e) => setMaxWorkersKm(e.target.value)}
                    className="w-28"
                  />
                </div>
                <Button onClick={savePerformance} isLoading={isSaving}>
                  <Save className="w-4 h-4 mr-1" /> Сохранить
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-start gap-4">
            <div className="p-2 rounded-lg bg-rose-50 text-rose-600">
              <ScanLine className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-semibold text-gray-900">Обработка дублей при сканировании</h2>
              <p className="text-sm text-gray-600 mt-1">
                Что делать, если воркер отсканировал КМ, который уже есть в задании.
              </p>
              <div className="mt-4 space-y-2">
                <label className="flex items-start gap-2 text-sm cursor-pointer">
                  <input
                    type="radio"
                    name="scan-duplicate-mode"
                    className="mt-1"
                    checked={scanDuplicateMode === 'reject'}
                    onChange={() => setScanDuplicateMode('reject')}
                  />
                  <span>
                    <span className="font-medium text-gray-900">Блокировка</span>
                    <span className="block text-gray-500">Отклонять скан и показывать, где лежит оригинал (паллета / коробка).</span>
                  </span>
                </label>
                <label className="flex items-start gap-2 text-sm cursor-pointer">
                  <input
                    type="radio"
                    name="scan-duplicate-mode"
                    className="mt-1"
                    checked={scanDuplicateMode === 'highlight'}
                    onChange={() => setScanDuplicateMode('highlight')}
                  />
                  <span>
                    <span className="font-medium text-gray-900">Визуализация</span>
                    <span className="block text-gray-500">Показывать дубль красным в UI воркера; у админа — журнал с паллетой, коробкой и кодом.</span>
                  </span>
                </label>
              </div>
              <Button className="mt-4" onClick={saveScanDuplicateMode} isLoading={isSavingScan}>
                <Save className="w-4 h-4 mr-1" /> Сохранить
              </Button>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-start gap-4">
            <div className="p-2 rounded-lg bg-indigo-50 text-indigo-600">
              <Printer className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-semibold text-gray-900">Режим печати</h2>
              <p className="text-sm text-gray-600 mt-1">
                Как отправлять этикетки на принтер из задания.
              </p>
              <div className="mt-4 space-y-2">
                <label className="flex items-start gap-2 text-sm cursor-pointer">
                  <input
                    type="radio"
                    name="print-method"
                    className="mt-1"
                    checked={printMethod === 'agent'}
                    onChange={() => setPrintMethod('agent')}
                  />
                  <span>
                    <span className="font-medium text-gray-900">Агент</span>
                    <span className="block text-gray-500">
                      Локальный print-agent на ПК: рендер на сервере, печать через драйвер Windows.
                    </span>
                  </span>
                </label>
                <label className="flex items-start gap-2 text-sm cursor-pointer">
                  <input
                    type="radio"
                    name="print-method"
                    className="mt-1"
                    checked={printMethod === 'browser'}
                    onChange={() => setPrintMethod('browser')}
                  />
                  <span>
                    <span className="font-medium text-gray-900">Браузер</span>
                    <span className="block text-gray-500">
                      Предпросмотр в модалке и системный диалог печати браузера.
                    </span>
                  </span>
                </label>
              </div>
              <Button className="mt-4" onClick={savePrintMethod} isLoading={isSavingPrint}>
                <Save className="w-4 h-4 mr-1" /> Сохранить
              </Button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white rounded-lg border border-gray-200 p-6 h-full">
            <div className="flex items-start gap-4">
              <div className="p-2 rounded-lg bg-blue-50 text-blue-600">
                <Tags className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <h2 className="text-lg font-semibold text-gray-900">Шаблоны этикеток</h2>
                <p className="text-sm text-gray-600 mt-1">
                  Список шаблонов печати, назначение для КМ, КА коробок и КА паллет, шаблоны по умолчанию.
                </p>
                <Link to="/admin/settings/label-templates" className="inline-block mt-4">
                  <Button>
                    Открыть шаблоны этикеток <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-6 h-full">
            <div className="flex items-start gap-4">
              <div className="p-2 rounded-lg bg-amber-50 text-amber-600">
                <Layers className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <h2 className="text-lg font-semibold text-gray-900">Шаблоны КА (AI)</h2>
                <p className="text-sm text-gray-600 mt-1">
                  GS1-структура кодов агрегации: порядок AI, FNC1, SSCC для коробок и паллет.
                </p>
                <Link to="/admin/ka-templates" className="inline-block mt-4">
                  <Button>
                    Открыть шаблоны КА <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-start gap-4">
            <div className="p-2 rounded-lg bg-emerald-50 text-emerald-600">
              <Boxes className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-semibold text-gray-900">Товарные группы и структуры КМ</h2>
              <p className="text-sm text-gray-600 mt-1">
                Настройка структур кодов маркировки по товарным группам для scan-режима и проверки КМ.
              </p>
              <Link to="/admin/settings/code-structures" className="inline-block mt-4">
                <Button>
                  Открыть товарные группы <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
